import React, { Fragment, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { noop } from '@lux/helpers';
import notifications from '../../config/notifications';
import validationMessages from '../../config/validation-messages.json';
import { ChooseSIMType } from '../ChooseSIMType';
import {
  TextInput,
  Text,
  Modal,
  Spacing,
  Row,
  Column,
  Button,
  useDevice,
  DropdownInput
} from '@dls/web';
import { FieldObject } from '../../types/registrationCheckout';
import { isValidPhoneNumber } from '../../helpers/common';
import { getRawPhoneNumber } from '../Checkout/helper';
import { trans as t } from '../../helpers/localisation';
import { ChooseSimType } from '../../constants/choose-sim-type';

interface State {
  hintMessage?: string;
  number?: string;
  termsModalOpen?: boolean;
  errorModalOpen?: boolean;
  showErrorStatus?: boolean;
  dropDownHintMessage?: string;
}

interface PropTypes {
  errorStatus: string | boolean;
  onClick: (
    number: string,
    selectedTelcoService: string,
    selectedSIMType?: string
  ) => void;
  onModalClose: () => void;
  chooseSIMOptions: ChooseSimType[];
  selectedProduct: FieldObject;
  skipAddons?: boolean;
}

const NumberPortIn = (props: PropTypes) => {
  const { isMobile } = useDevice();

  const {
    errorStatus,
    onClick,
    onModalClose,
    chooseSIMOptions,
    selectedProduct,
    skipAddons
  } = props;

  const [state, _setState] = useState<State>({
    hintMessage: '',
    number: undefined,
    termsModalOpen: false,
    errorModalOpen: false,
    showErrorStatus: false
  });

  const portInDisplayList = [
    {
      text: 'M1 Postpaid',
      value: 'M1'
    },
    {
      text: 'Starhub Postpaid',
      value: 'STARHUB'
    },
    {
      text: 'Simba Postpaid',
      value: 'SIMBA'
    },
    {
      text: 'GOMO Postpaid',
      value: 'GOMO'
    },
    {
      text: 'hi! Postpaid',
      value: 'HIPOSTPAID'
    },
    {
      text: 'Others',
      value: 'OTHERS'
    }
  ];

  const [selectedTelcoService, setSelectedTelcoService] = useState<
    typeof portInDisplayList[number]['value'] | void
  >();

  const setState = (stateChange: Partial<State>) => {
    _setState(prevState => ({ ...prevState, ...stateChange }));
  };

  const validateInput = useCallback(
    (
      number: string,
      selectedTelcoService: string,
      selectedSIMType?: string
    ) => {
      setState({ hintMessage: '', dropDownHintMessage: '' });
      const isTelcoExists =
        selectedTelcoService &&
        portInDisplayList.some(item => item?.value === selectedTelcoService);
      if (!isValidPhoneNumber(number)) {
        setState({
          hintMessage: validationMessages.validMobileStartingWithEight
        });
        return false;
      } else if (!isTelcoExists) {
        setState({
          dropDownHintMessage: validationMessages.selectYourCurrentTelco
        });
        return false;
      }

      onClick(getRawPhoneNumber(number), selectedTelcoService, selectedSIMType);
    },
    [onClick]
  );

  const handleButtonClick = (selectedSIMType?: string) => {
    validateInput(state.number, selectedTelcoService, selectedSIMType);
    setState({ showErrorStatus: true });
  };

  useEffect(() => {
    if (errorStatus && state.showErrorStatus) {
      setState({ errorModalOpen: true });
    }
  }, [errorStatus, state.showErrorStatus]);

  const errorMessage = errorStatus && notifications[errorStatus];

  const ErrorMessageModal = () => {
    const handleModalClose = () => {
      setState({ number: '', errorModalOpen: false, showErrorStatus: false });
      onModalClose();
    };

    return (
      errorMessage && (
        <Modal
          data-testid="error-modal"
          visible={state.errorModalOpen}
          title={errorMessage && errorMessage.title}
          onClose={handleModalClose}
        >
          <Modal.Content>
            <Spacing bottom={3} bottomMd={5}>
              <Text type="body">{errorMessage.text}</Text>
            </Spacing>
            <Button onClick={handleModalClose}>{errorMessage.ctaText}</Button>
          </Modal.Content>
        </Modal>
      )
    );
  };

  const onNumberChanged = e => {
    const number = e.target.value;
    if (isValidPhoneNumber(number)) {
      return setState({ number, hintMessage: undefined });
    }

    return setState({
      hintMessage: validationMessages.validMobileStartingWithEight,
      number
    });
  };

  const handleSetSelectedTelcoChange = useCallback(
    (nextSelectedTelcoService: typeof selectedTelcoService) => {
      if (nextSelectedTelcoService === selectedTelcoService) {
        return; // do nothing if telco service is already selected
      }
      setSelectedTelcoService(nextSelectedTelcoService);
    },
    [selectedTelcoService]
  );

  // Handle telco service dropdown option change
  const onPortinDropdownChange = useCallback(
    item => {
      handleSetSelectedTelcoChange(item.value);
    },
    [handleSetSelectedTelcoChange]
  );

  return (
    <Fragment>
      <Row>
        <Column sm={12} md={12} noGutter>
          <Spacing top={isMobile ? 1 : 0.1} bottom={isMobile ? 3 : 2}>
            <Text type="header">
              {t('PORTIN_TRANSFER_MY_NUMBER_TO_SINGTEL')}
            </Text>
          </Spacing>
          <Spacing bottom={1}>
            <DropdownInput
              bgColor="haze"
              id="serviceDropdown"
              label={t('YOUR_CURRENT_TELCO')}
              onChange={onPortinDropdownChange}
              items={portInDisplayList}
              value={selectedTelcoService}
            />
          </Spacing>
          <Spacing bottom={1}>
            <TextInput
              displayFormat="mobile-sg"
              bgColor={'haze'}
              label={t('YOUR_EXISTING_NUMBER')}
              value={state.number}
              maxLength={13}
              onChange={onNumberChanged}
              validation={state.hintMessage && 'danger'}
              error={!!state.hintMessage}
              hintMessage={state.hintMessage}
            />
          </Spacing>
        </Column>
      </Row>
      {isValidPhoneNumber(state.number) && selectedTelcoService && (
        <ChooseSIMType
          isPortIn
          data={chooseSIMOptions}
          scrollToSimType
          transactionTypeValue="portIn"
          onSimTypeOnSelection={handleButtonClick}
          selectedProduct={selectedProduct}
          btnText={skipAddons ? t('PROCEED') : t('CHOOSE_ADD_ONS')}
        />
      )}

      {state.errorModalOpen && ErrorMessageModal()}
    </Fragment>
  );
};

NumberPortIn.defaultProps = {
  onClick: noop,
  onModalClose: noop
};

export default NumberPortIn;
